/**
 * @overload
 * * This is an overloaded function that checks if a string, array, or object `isEmpty`.
 */

/**
 * Checks if a string is empty (including strings with only whitespace characters).
 * @param value - The string to check.
 *
 * @note We also consider a string to be empty if it only has whitespace characters.
 * @returns True if the string is empty, false otherwise.
 */
export function isEmpty(value: string): boolean;

/**
 * Checks if an array is empty.
 * @param value - The array to check.
 * @returns `true` if the array is empty, `false` otherwise.
 */
export function isEmpty<Item>(value: Item[]): boolean;

/**
 * Checks if a Set is empty.
 * @param value - The Set to check.
 * @returns `true` if the Set is empty, `false` otherwise.
 */
export function isEmpty<SetItem>(value: Set<SetItem>): boolean;

/**
 * Checks if a Map is empty.
 * @param value - The Map to check.
 * @returns `true` if the Map is empty, `false` otherwise.
 */
export function isEmpty<KeyType, ValueType>(value: Map<KeyType, ValueType>): boolean;

/**
 * Checks if an object is empty.
 * @param value - The object to check.
 * @returns `true` if the object is empty, `false` otherwise.
 * @throws {TypeError} If `value` is `null` or `undefined`.
 */
export function isEmpty<ObjectType extends object>(value: ObjectType): boolean;

/**
 * Implementation of the isEmpty function.
 * @param value - The value to check.
 * @returns `true` if the value is considered empty, `false` otherwise.
 */
export function isEmpty(value: string | object | unknown[]): boolean {
    // if invalid value is passed we'll return true
    // with strict types this should never happen
    // ? Review: remove this check once strict types are enabled
    if (typeof value !== "string" && !Array.isArray(value) && typeof value !== "object") {
        return true;
    }

    // Check if the value is an empty string
    if (typeof value === "string") {
        return value.trim().length === 0;
    }

    // Check if the value is an empty array
    if (Array.isArray(value)) {
        return value.length === 0;
    }

    if (typeof value === "object") {
        // Check if the value is an empty set
        if (value instanceof Set) {
            return value.size === 0;
        }

        // Check if the value is an empty map
        if (value instanceof Map) {
            return value.size === 0;
        }

        // Check if the value is an empty object
        return Object.keys(value).length === 0;
    }

    return false;
}

/**
 * @function
 * *This is function that checks if a value is null or undefined.
 */


/**
 *
 * Checks if value is null or undefined
 * @param value the value to check
 */
export const isNil = <T>(value: T | null | undefined): value is null | undefined =>
    value === null || value === undefined;

/**
 * @overload
 * *This is an overloaded function that checks if value `isBlank`.
 */

/**
 * Checks if a string is blank.
 * A string is considered blank if it is empty or consists only of whitespace characters.
 *
 * @param value - The string to check.
 * @returns `true` if the string is blank, `false` otherwise.
 */
export function isBlank(value: string): boolean;

/**
 * Checks if a number is blank.
 * A number is not considered if it has a value of 0.
 *
 * @param value - The string to check.
 * @returns `true` if the string is blank, `false` otherwise.
 */
export function isBlank(value: number): boolean;

/**
 * Checks if an array is blank.
 * An array is considered blank if it is empty or all its elements are blank.
 *
 * @param value - The array to check.
 * @returns `true` if the array is blank, `false` otherwise.
 */
export function isBlank<T>(value: T[]): boolean;

/**
 * Checks if an object is blank.
 * An object is considered blank if it is empty or all its properties are blank.
 *
 * @param value - The object to check.
 * @returns `true` if the object is blank, `false` otherwise.
 */
export function isBlank<T extends object>(value: T): boolean;

/**
 * Implementation of the isBlank function.
 *
 * @param value - The value to check.
 * @returns `true` if the value is considered blank, `false` otherwise.
 */
export function isBlank<T>(value: T): boolean {
    if (isNil(value)) {
        return true;
    }

    if (typeof value === "number") {
        return false;
    }

    if (!value) {
        return true;
    }

    if (typeof value === "object") {
        return isEmpty(value);
    }

    if (typeof value === "string") {
        return isEmpty(value);
    }

    return false;
}

/**
 * @overload
 * *This is an overloaded function that checks if value `isPresent`.
 */

/**
 * Checks if an object is present (not null, undefined, or empty).
 * @param value - The value to check.
 */
export function isPresent<Type>(value: Type): value is NonNullable<Type>;

/**
 * Checks if an object is present (not null, undefined, or empty).
 * @param value - The object to check.
 */
export function isPresent<ObjectType extends object>(value: ObjectType): value is NonNullable<ObjectType> {
    return !isBlank(value);
}
